import store, { NAMESPACES } from '@/store'
import { UnitOfMeasure, UnitOfMeasureProperties } from '@fusion/units'
import {
  measurementFriendlyNameKey,
  measurementTypesUnitsKey,
  measurementImgSrc,
  MeasurementType,
  allMeasurementStatistics,
  measurementTypesOrder,
  sensorTypesKey,
  getMeasurementTypeKeyByName,
  SensorType
} from '@/services/device-measurements'
import { getMeasurementLabelKeyForAqi } from '@/store/map/utils'
import { getPermissionsByDeviceId } from '@/services/subscriptions'
import { getReadingValue, type Readings } from '@/services/unit-conversion'
import {
  getPMSelectedUnit,
  getTemperatureSelectedUnit,
  getPressureSelectedUnit
} from '@/services/user-settings'
import { DEVICE_GETTERS } from '@/store/devices'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import { MAP_GETTERS } from '@/store/map'
import type { FormattedMeasurement } from '@/services/device-service/devices'

export interface WidgetConfiguration {
  savedParameter?: number
  defaultParameter?: number
  label?: string
  customClass?: string
  showInactiveReading?: boolean
}

export interface WidgetDevice {
  id: string
  friendlyName: string
  model: string
  submodel: string
  isMine: boolean
  isShared: boolean
}
export interface PopoverMeasurementData {
  measurementName: string
  deviceType: string
  readings: Readings
}

export interface DataProperties {
  unitsLabel: string
  label: string
  imageSrc: string
  reading: FormattedMeasurement
}

export type PopoverMeasurementDataResult = DataProperties & WidgetConfiguration

export interface WidgetMeasurementOption {
  value: MeasurementType
  label: string
}

const getSelectedParameterFromConfiguration = (
  currentMeasurement: number,
  configuration: WidgetConfiguration | undefined
): number => {
  if (configuration) {
    if (typeof configuration.savedParameter === 'number') {
      return configuration.savedParameter
    }

    if (typeof configuration.defaultParameter === 'number') {
      return configuration.defaultParameter
    }
  }

  return currentMeasurement
}

export const getLabelUnitCode = (unit: UnitOfMeasure): string => {
  return UnitOfMeasureProperties[unit]?.code ?? ''
}

export const getPMLabelUnit = (): string => {
  const pmUnit = getPMSelectedUnit()
  return getLabelUnitCode(pmUnit)
}

export const getTemperatureLabelUnit = (): string => {
  const tempUnit = getTemperatureSelectedUnit()
  return getLabelUnitCode(tempUnit)
}

export const getPressureLabelUnit = (): string => {
  const pressureUnit = getPressureSelectedUnit()
  return getLabelUnitCode(pressureUnit)
}

export const getMeasurementSelectedUnit = (
  measurementId: MeasurementType,
  translateFn: (key: string) => string
): string => {
  const sensorType = sensorTypesKey[measurementId]
  const pmMeasurementsWithoutUnitLabel: MeasurementType[] = [
    MeasurementType.Pm25,
    MeasurementType.Pm10
  ]

  if (
    sensorType === SensorType.PM &&
    pmMeasurementsWithoutUnitLabel.indexOf(measurementId) === -1
  ) {
    return getPMLabelUnit()
  }

  switch (measurementId) {
    case MeasurementType.Temp:
      return getTemperatureLabelUnit()
    case MeasurementType.BaroInhg:
      return getPressureLabelUnit()
    default:
      return measurementTypesUnitsKey[measurementId]
        ? translateFn(measurementTypesUnitsKey[measurementId])
        : ''
  }
}

export const getPopoverMeasurementData = (
  translateFn: (key: string, params?: Record<string, unknown>) => string,
  data: PopoverMeasurementData = { measurementName: '', deviceType: '', readings: {} },
  configuration: WidgetConfiguration = {}
): PopoverMeasurementDataResult => {
  const { measurementName, deviceType, readings } = data
  const measurementId = getMeasurementTypeKeyByName(measurementName)

  return {
    unitsLabel: getMeasurementSelectedUnit(measurementId, translateFn),
    label: translateFn(measurementFriendlyNameKey[measurementId]) || '',
    imageSrc: measurementImgSrc[measurementId] || '',
    reading: getReadingValue(readings, measurementName, deviceType, true),
    ...configuration
  }
}

export const getWidgetSelectedDevices = (
  deviceIds: string[],
  accountId: string
): WidgetDevice[] => {
  if (deviceIds?.length) {
    return deviceIds.map((deviceId) => {
      return {
        id: deviceId,
        friendlyName:
          store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_NAME_BY_DEVICE_ID}`](deviceId),
        model:
          store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`](deviceId),
        submodel:
          store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SUBMODEL_BY_DEVICE_ID}`](
            deviceId
          ),
        isMine:
          accountId ===
          store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_ACCOUNT_BY_DEVICE_ID}`](
            deviceId
          ),
        isShared:
          store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_SHARED_BY_DEVICE_ID}`](
            deviceId
          )
      }
    })
  }

  return []
}

export const getWidgetAvailableMeasurements = (
  selectedDevices: WidgetDevice[],
  filterMeasurements: boolean,
  filterMeasurementStatistics: boolean
): MeasurementType[] => {
  // Build an array of unique measurements across all selected devices
  let measurements: MeasurementType[] = [
    ...new Set(
      selectedDevices
        .map((device) => {
          const isPublicOnly = !device.isMine && !device.isShared
          return store.getters[
            `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SUPPORTED_MEASUREMENTS}`
          ](device.model, device.submodel, isPublicOnly)
        })
        .flat(1)
    )
  ]

  // Filter measurements based on whether PM 2.5 or PM 10 is selected
  if (filterMeasurements) {
    measurements = measurements.filter((measurement) => {
      if (
        store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_MEASUREMENT_TYPE}`] ===
        MeasurementType.Pm25
      ) {
        return measurement !== MeasurementType.Pm10 && measurement !== MeasurementType.Pm10Raw
      } else {
        return measurement !== MeasurementType.Pm25 && measurement !== MeasurementType.Pm25Raw
      }
    })
  }

  if (filterMeasurementStatistics) {
    measurements = measurements.filter((measurement) => {
      return allMeasurementStatistics.indexOf(measurement) === -1
    })
  }

  // Sort the array by the defined sort array
  return measurements.sort(function (a, b) {
    return measurementTypesOrder.indexOf(a) - measurementTypesOrder.indexOf(b)
  })
}

export const getMeasurementLabelKey = (measurement: MeasurementType): string => {
  const aqiType = store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_AQI_TYPE}`]
  return getMeasurementLabelKeyForAqi(measurement, aqiType)
}

export const getWidgetMeasurementOptions = (
  translateFn: (key: string) => string,
  availableMeasurements: MeasurementType[] | null | undefined
): WidgetMeasurementOption[] => {
  if (availableMeasurements?.length) {
    return availableMeasurements.map((measurement) => {
      return {
        value: measurement,
        label: translateFn(`map.historyDialog.dropdown.${getMeasurementLabelKey(measurement)}`)
      }
    })
  }

  return []
}

export const getDevicesPermissionSet = (selectedDevices: WidgetDevice[]): string[] => {
  if (selectedDevices?.length) {
    const perms: Record<string, string[]> = {}

    for (const device of selectedDevices) {
      const deviceId = device.id
      perms[deviceId] = getPermissionsByDeviceId(deviceId)
    }

    return [...new Set(selectedDevices.map((device) => perms[device.id]).flat(1))]
  }

  return []
}

export const getSelectedMeasurement = (
  currentMeasurement: number,
  availableMeasurements: number[],
  configuration?: WidgetConfiguration
): number => {
  const newSelectedMeasurement = getSelectedParameterFromConfiguration(
    currentMeasurement,
    configuration
  )

  if (availableMeasurements?.length && !availableMeasurements.includes(newSelectedMeasurement)) {
    return availableMeasurements[0]
  }

  return newSelectedMeasurement
}
